import React from 'react';
import { type KeyboardShortcut } from '@/hooks/useKeyboardShortcuts';

function KeyBox({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-4 h-4 px-1 bg-muted border border-border rounded shadow-xs text-xs font-medium text-muted-foreground">
      {children}
    </kbd>
  );
}

interface ShortcutIndicatorProps {
  shortcut: Pick<KeyboardShortcut, 'key' | 'modifiers'>;
  variant?: 'default' | 'compact' | 'minimal';
  className?: string;
}

export function ShortcutIndicator({
  shortcut,
  variant = 'default',
  className = '',
}: ShortcutIndicatorProps) {
  const formatKeyWithBoxes = (
    shortcut: Pick<KeyboardShortcut, 'key' | 'modifiers'>
  ) => {
    const parts: string[] = [];

    if (shortcut.modifiers?.ctrl) parts.push('Ctrl');
    if (shortcut.modifiers?.alt) parts.push('Alt');
    if (shortcut.modifiers?.shift) parts.push('Shift');
    if (shortcut.modifiers?.meta) parts.push('⌘');

    parts.push(
      shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key
    );

    return parts;
  };

  if (variant === 'minimal') {
    return (
      <span className={`text-xs text-muted-foreground ${className}`}>
        {shortcut.key.toUpperCase()}
      </span>
    );
  }

  if (variant === 'compact') {
    const keys = formatKeyWithBoxes(shortcut);
    return (
      <span className={`inline-flex items-center gap-0.5 ${className}`}>
        {keys.map((key, index) => (
          <React.Fragment key={key}>
            {index > 0 && (
              <span className="text-xs text-muted-foreground mx-0.5">+</span>
            )}
            <KeyBox>{key}</KeyBox>
          </React.Fragment>
        ))}
      </span>
    );
  }

  const keys = formatKeyWithBoxes(shortcut);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {keys.map((key, index) => (
        <React.Fragment key={key}>
          {index > 0 && (
            <span className="text-xs text-muted-foreground mx-1">+</span>
          )}
          <KeyBox>{key}</KeyBox>
        </React.Fragment>
      ))}
    </span>
  );
}

import { Button, type ButtonProps } from '@/components/ui/button';

interface ShortcutButtonProps extends ButtonProps {
  shortcut?: Pick<KeyboardShortcut, 'key' | 'modifiers'>;
  showShortcut?: boolean;
  shortcutVariant?: 'default' | 'compact' | 'minimal';
  children: React.ReactNode;
}

export function ShortcutButton({
  shortcut,
  showShortcut = true,
  shortcutVariant = 'compact',
  children,
  ...props
}: ShortcutButtonProps) {
  return (
    <Button {...props}>
      {children}
      {shortcut && showShortcut && (
        <ShortcutIndicator shortcut={shortcut} variant={shortcutVariant} />
      )}
    </Button>
  );
}
